import type {
  CommerceClient,
  Cart,
  PaymentPreparationResult,
  PaymentRedirectStartResult,
  UsablePaymentMethodWebDescriptor,
} from '@altazion/commerce-sdk-core'

import {
  asRecord,
  requireNumber,
  requireString,
  type AltazionModuleDefinition,
  type AltazionSuccessEventKind,
} from '../altazion-engine'

export type AltazionPaymentsActionName = 'getChoicesPrivate' | 'preparePaymentPrivate' | 'registerPaymentPrivate' | 'startRedirectPayment'

export type PaymentsActionResult = UsablePaymentMethodWebDescriptor[] | PaymentPreparationResult | Cart | PaymentRedirectStartResult

export const paymentsModuleDefinition: AltazionModuleDefinition<'payments', AltazionPaymentsActionName, PaymentsActionResult> = {
  moduleName: 'payments',
  actionAttribute: 'hx-altazion-payments-action',
  payloadAttributes: {
    'hx-altazion-payments-method-guid': 'paymentMethodGuid',
    'hx-altazion-payments-amount': 'amount',
    'hx-altazion-payments-reference-number': 'referenceNumber',
    'hx-altazion-payments-auto-validated': 'autoValidated',
    'hx-altazion-payments-return-url': 'returnUrl',
  },
  jsonPayloadAttributes: {
    'hx-altazion-payments-input-data': 'inputData',
  },
  payloadAliases: {
    methodGuid: 'paymentMethodGuid',
    methodId: 'paymentMethodGuid',
    amountToPay: 'amount',
    returnTo: 'returnUrl',
  },
  normalizePayload(payload: Record<string, unknown>): void {
    if (typeof payload.autoValidated === 'string') {
      payload.autoValidated = payload.autoValidated.trim().toLowerCase() === 'true'
    }

    const returnUrl = typeof payload.returnUrl === 'string' ? payload.returnUrl.trim() : ''
    if (returnUrl.length > 0) {
      const inputData = asRecord(payload.inputData) ?? {}
      payload.inputData = {
        ...inputData,
        returnUrl,
      }
    }
  },
  parseAction(actionValue: string): AltazionPaymentsActionName {
    switch (actionValue) {
      case 'getChoicesPrivate':
      case 'preparePaymentPrivate':
      case 'registerPaymentPrivate':
      case 'startRedirectPayment':
        return actionValue
      default:
        throw new Error(`Unsupported payments action: ${actionValue}`)
    }
  },
  async execute(client: CommerceClient, action: AltazionPaymentsActionName, payload: Record<string, unknown>): Promise<PaymentsActionResult> {
    switch (action) {
      case 'getChoicesPrivate':
        return client.payments.getChoicesPrivate()
      case 'preparePaymentPrivate':
        return client.payments.preparePaymentPrivate(buildPreparationRequest(payload))
      case 'registerPaymentPrivate':
        return client.payments.registerPaymentPrivate(buildPaymentRequest(payload))
      case 'startRedirectPayment': {
        const result = await client.payments.startRedirectPayment(buildPreparationRequest(payload))
        if (typeof window !== 'undefined') {
          window.location.assign(result.redirectUrl)
        }
        return result
      }
    }
  },
  successEventKind(action: AltazionPaymentsActionName): AltazionSuccessEventKind {
    switch (action) {
      case 'registerPaymentPrivate':
      case 'startRedirectPayment':
        return 'changed'
      case 'getChoicesPrivate':
      case 'preparePaymentPrivate':
        return 'loaded'
    }
  },
  getResultEventSuffixes(action: AltazionPaymentsActionName): string[] {
    switch (action) {
      case 'getChoicesPrivate':
        return ['choices-loaded']
      case 'preparePaymentPrivate':
        return ['prepared']
      case 'registerPaymentPrivate':
        return ['registered']
      case 'startRedirectPayment':
        return ['redirect-started']
    }
  },
}

function buildPreparationRequest(payload: Record<string, unknown>) {
  const request = buildPaymentRequest(payload) as { paymentMethodGuid: string; amount: number; referenceNumber?: string; autoValidated?: boolean; inputData?: Record<string, string> }
  request.inputData = readInputData(payload)
  return request
}

function buildPaymentRequest(payload: Record<string, unknown>) {
  return {
    paymentMethodGuid: requireString(payload.paymentMethodGuid, 'paymentMethodGuid'),
    amount: requireNumber(payload.amount, 'amount'),
    referenceNumber: typeof payload.referenceNumber === 'string' && payload.referenceNumber.trim().length > 0
      ? payload.referenceNumber.trim()
      : undefined,
    autoValidated: payload.autoValidated === true,
  }
}

function readInputData(payload: Record<string, unknown>): Record<string, string> | undefined {
  const inputData = asRecord(payload.inputData)
  if (!inputData) {
    return undefined
  }

  const entries = Object.entries(inputData)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length > 0)
    .map(([key, value]) => [key, value.trim()] as const)

  return entries.length > 0
    ? Object.fromEntries(entries)
    : undefined
}